import { getFhirIdFromEntity } from 'src/utils/fhir';
import { useEffect, useMemo, useState } from 'react';
import { QuestionnaireAnswerMap, ReportResources } from 'src/careflow/inck/nat-task/report/report-type';
import { TaskWrapper } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Task';
import { PatientWrapper, WrappedPatient } from "src/careflow/ph-core/fhir-client/fhir-base/wrappers/Patient";
import { 
  Bundle, 
  MeasureReport, 
  Organization, 
  Patient, 
  Practitioner, 
  Questionnaire, 
  QuestionnaireResponse, 
  RelatedPerson,
  ServiceRequest, 
  Task, 
  TaskInput, 
  TaskOutput 
} from 'src/careflow/ph-core/fhir-client/resources';
import { ObservationWrapper, WrappedObservation } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Observation';
import { useObservations, usePlanDefinition, useServiceRequest, useTasks } from 'src/careflow/ph-core/fhir-client/fhir-react';
import { PlanDefinitionWrapper, WrappedPlanDefinition } from "src/careflow/ph-core/fhir-client/fhir-base/wrappers/PlanDefinition";
import { ServiceRequestWrapper, WrappedServiceRequest } from "src/careflow/ph-core/fhir-client/fhir-base/wrappers/ServiceRequest";
import { getUserByEmail } from 'src/ccm/services/api/users';
import { isEmpty } from 'lodash';
import { TaskService } from 'src/careflow/inck/nat-task/service/TaskService';
import { getPatientDetailsFromQR } from 'src/careflow/inck/nat-task/helpers/report';
import { fhirClient } from 'src/App';

const useTaskServiceRequestQuestionnaire = ( taskId: string ): { 
  patient: WrappedPatient | undefined,
  report: ReportResources[] | undefined, 
  planDefinition: WrappedPlanDefinition | null, 
  serviceRequest: WrappedServiceRequest | null,
  observations: WrappedObservation[] | undefined;
  updateServiceRequest: any,
  refreshServiceRequest: any,
  isLoading: boolean,
} => {

  const [ patientFromTask, setPatientFromTask ] = useState<Patient | undefined>(); 
  const [ report, setReport ] = useState<ReportResources[] | undefined>(); 
  const [ serviceRequestFromTask, setServiceRequestFromTask ] = useState<ServiceRequest | undefined>(); 
  const [ isLoadingReport, setIsLoadingReport ] = useState(false); 

  const taskPatientServiceRequest = async () => (( await fhirClient.search(
    `Task?_id=${taskId}&code=completed-nat-assessment&_include:iterate=Task:patient:Patient&_include:iterate=Task:based-on:ServiceRequest`,
  )) as Bundle<Task | Patient | ServiceRequest>)?.
  entry?.map((entry) => entry?.resource);

  useEffect(() => {
    const taskResources = async () => {
      if(taskId) {
        const getResources = await taskPatientServiceRequest();
        setPatientFromTask(getResources?.find((resource): resource is Patient => resource?.resourceType === 'Patient'));
        setServiceRequestFromTask(getResources?.find((resource): resource is ServiceRequest => resource?.resourceType === 'ServiceRequest'));
      }
    };
    taskResources();
  }, [taskId]);
  
  const [ serviceRequest,{ 
    update: updateServiceRequest, 
    refresh: refreshServiceRequest,
    isLoading: isLoadingServiceRequest,
  }] =  useServiceRequest(serviceRequestFromTask?.id, {
    map: ServiceRequestWrapper,
    autofetch: !!serviceRequestFromTask
  });

  const patient = patientFromTask && PatientWrapper(patientFromTask);

  const planDefinitionId = useMemo(() => 
    getFhirIdFromEntity(serviceRequestFromTask?.instantiatesCanonical?.[0] ?? ''), [serviceRequest]
  );

  const [ planDefinition ] =  usePlanDefinition(planDefinitionId, {
    map: PlanDefinitionWrapper,
    autofetch: !!planDefinitionId,
  });

  const [ tasks, { isLoading: isLoadingTasks }  ] = useTasks({
    filter: {
      'based-on': serviceRequestFromTask?.id,
      'code': 'completed-nat-assessment'
    },
    map: TaskWrapper,
    autofetch: !!serviceRequestFromTask
  });

  useEffect(() => {
    const fetchData = async () => {
      const data = await getQuestionnaireAndResponses();
      setReport(data);
    };
    fetchData();
  }, [serviceRequestFromTask, tasks]);

  const getQuestionnaireAndResponses = async (): Promise<any> => {
    try {
      setIsLoadingReport(true);
      const report = await Promise.all(tasks?.map( async (item) => {
        const questionnaireFromInput: string[] = item?.input?.
          filter((x1: TaskInput) => x1?.valueUri?.includes('Questionnaire')).
            map((x2) => getFhirIdFromEntity(x2?.valueUri ?? '') ?? '') ?? [];
  
        const questionnaireResponseFromOutput: string[] = item?.output?.
          filter((x1: TaskOutput) => x1?.valueReference?.type === 'QuestionnaireResponse').
            map((x2) => getFhirIdFromEntity(x2?.valueReference?.reference ?? '') ?? '') ?? [];
            
        const measureReportFromOutput: string = item?.output?.
          find((x1: TaskOutput) => x1?.type?.coding?.[0]?.code === 'measureReport')?.valueUri ?? '';
  
        const questionnaire = questionnaireFromInput?.length ? ((await fhirClient.search(
            `Questionnaire?_id=${questionnaireFromInput?.join(',')}`,
          )) as Bundle<Questionnaire>)?.
        entry?.map((entry) => entry?.resource) : [];

        const questionnaireAnswerMap = questionnaireFromInput?.length ? (await TaskService.getQuestionnaireAnswerMap(
          questionnaireFromInput)) as QuestionnaireAnswerMap[] : [];
  
        const questionnaireResponse = questionnaireResponseFromOutput?.length ? ((await fhirClient.search(
            `QuestionnaireResponse?_id=${questionnaireResponseFromOutput?.join(',')}`,
          )) as Bundle<QuestionnaireResponse>)?.
        entry?.map((entry) => entry?.resource) : [];
  
        const measureReport = !isEmpty(measureReportFromOutput) ? (await fhirClient.findById(
          'MeasureReport',`${getFhirIdFromEntity(measureReportFromOutput) ?? ''}`,
        ))as MeasureReport : undefined;
  
        const ownerTask = !isEmpty(item?.owner) && (await fhirClient.getResource(
          `${item?.owner?.reference}`,
        )) as RelatedPerson | Patient;
  
        const requesterTaskResource = !isEmpty(item?.requester) && (await fhirClient.getResource(
          `${item?.requester?.reference}`,
        )) as Practitioner | Organization;

        let requesterTask;
        if(requesterTaskResource && requesterTaskResource?.resourceType === 'Organization'){
          const userEmailFromTask = item?.identifier?.find((item) => item?.type?.text === 'User')?.value;
          const userFromMetaTag = item?.meta?.tag?.find((e) => e?.code === "ph-created-by")?.display;
          const getUser = await getUserByEmail(userEmailFromTask ?? userFromMetaTag ?? "");
          requesterTask = {
            resource: requesterTaskResource, 
            email: userEmailFromTask ?? userFromMetaTag ?? "",
            name: getUser?.id ? `${getUser?.firstName} ${getUser?.lastName}` : ""
          };
        }else {
          requesterTask = { resource: requesterTaskResource }
        }

        const patientDetailsFromReport = getPatientDetailsFromQR(
          questionnaire,
          questionnaireResponse,
          ["race","ethnicity","other_languages_spoken"],
          "demographics"
        );
        
        return {
          task: item,
          ownerTask: ownerTask,
          requesterTask: requesterTask,
          measureReport: measureReport,
          questionnaire: questionnaire, 
          questionnaireResponse: questionnaireResponse, 
          questionnaireAnswerMap: questionnaireAnswerMap,
          patientDetailsFromReport
        }
      }));
      return report;
    } catch (error) {
      console.log('useTaskServiceRequestQuestionnaire',error)
    }
    finally{
      setIsLoadingReport(false);
    }
  };  

  const [ observations, { isLoading: isLoadingObservations } ] = useObservations({ 
    filter: { subject: patient?.id }, 
    map: ObservationWrapper,
    autofetch: !!patient
  });

  const isLoading = useMemo(() => 
    (serviceRequestFromTask !== undefined ? isLoadingServiceRequest : false) || 
    (serviceRequestFromTask !== undefined ? isLoadingTasks : false) || 
    (patient !== undefined ? isLoadingObservations : false ) || 
    isLoadingReport
  ,[
    serviceRequestFromTask,
    patient,
    isLoadingServiceRequest,
    isLoadingTasks, 
    isLoadingObservations,
    isLoadingReport
  ]);

  return { 
    patient,
    report ,
    planDefinition, 
    serviceRequest, 
    observations,
    updateServiceRequest,
    refreshServiceRequest,
    isLoading
  }
}

export default useTaskServiceRequestQuestionnaire;